import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "../api/queryClient";
import AuthenticationStore from "../auth";
import { ZITADEL_RELOAD_DELAY } from "../constants";
import { ZitadelRoles } from "../enums";
import { adminServiceV1, adminServiceV2 } from "../services";

const AS = AuthenticationStore();

export interface UserObject {
  email?: string | undefined;
  firstName: string;
  lastName: string;
  isAdmin?: boolean;
  userId: string;
}

interface NewUserFormData {
  userName: string;
  profile: {
    firstName: string;
    lastName: string;
  };
  email: {
    email: string;
  };
  isAdmin: boolean;
}

interface NewUserData {
  formData: NewUserFormData;
}

interface ProjectInfo {
  id?: string;
  roleKeys: string[];
  projectId: string;
  projectGrantId: string;
  userId?: string;
}

interface MyProjectResponse {
  result: ProjectInfo[];
}

interface NewUserImportResponse {
  userId: string;
  details: {
    sequence: string;
    updatedAt: string;
    resourceOwner: string;
  };
}

interface UserFormData {
  firstName: string;
  lastName: string;
  displayName: string;
}

interface DeactivateUserPayload {
  userId: string;
}

interface EditRolePayload {
  isUserAdmin: boolean;
  userId: string;
}

interface UserMetaDetail {
  changeDate: string;
  updatedAt: string;
  resourceOwner: string;
  sequence: string;
}

interface ActiveUser {
  details: UserMetaDetail;
  human: {
    email: {
      email: string;
    };
    phone: undefined;
    profile: {
      displayName: string;
      givenName: string;
      familyName: string;
      preferredLanguage: string;
    };
    id: string;
    loginNames: string[];
    preferredLoginName: string;
    state: string;
    userName: string;
  };
  id: string;
  state: string;
  preferredLoginName: string;
  userId?: string;
}

export interface ActiveUserData {
  email: string;
  firstName: string;
  fullName: string;
  id: string;
  isAdmin: boolean;
  lastName: string;
  orgId: string;
  status: string;
  updatedAt: string;
  userId: string;
}

interface MemberList {
  displayName: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  userId: string;
}

interface OrgMembershipList {
  id: string;
  isAdmin: boolean;
  userId: string;
}

export const adminQueryKeys = {
  all: ["admin"],
  orgs: ["admin", "orgs"],
  item: (id: string | number) => [...adminQueryKeys.all, id],
};

async function getUserGrants() {
  const myProjectData: MyProjectResponse = await adminServiceV1.post("/users/grants/_search");
  return myProjectData?.result;
}

async function searchGrantedProjects() {
  const myGrantedProjectsData = await adminServiceV1.post("/granted_projects/_search");
  return myGrantedProjectsData?.result;
}

export async function getOrgMembers(): Promise<OrgMembershipList[]> {
  const userGrants = await getUserGrants();
  return adminServiceV1.post("/orgs/me/members/_search").then((response) =>
    response.result.map((item: MemberList) => {
      const isUserGrantCustomerAdmin = userGrants
        .find((userGrant) => userGrant.userId === item.userId)
        ?.roleKeys?.includes(ZitadelRoles.CUSTOMER_ADMIN);

      return {
        id: item.userId,
        isAdmin: !!item.roles?.includes(ZitadelRoles.ADMIN) && isUserGrantCustomerAdmin,
        userId: item.userId,
      };
    }),
  );
}

type OrgNameId = {
  label: string;
  value: string;
};

type OrgType = {
  details: {
    changeDate: string;
    resourceOwner: string;
    sequence: string;
  };
  id: string;
  name: string;
  primaryDomain: string;
  state: string;
};

type ProjectType = {
  details: {
    changeDate: string;
    creationDate: string;
    resourceOwner: string;
    sequence: string;
  };
  grantId: string;
  grantOrgId: string;
  grantOrgName: string;
  grantedRoleKeys: string[];
  projectId: string;
  projectName: string;
  projectOwnerId: string;
  projectOwnerName: string;
  state: string;
};

export async function getOrginizations() {
  const orgs = await adminServiceV2.post("organizations/_search");
  return orgs.result?.map((org: OrgType) => ({ label: org.name, value: org.id })) as OrgNameId[];
}

export const useGetActiveUsers = (isAdmin: boolean) =>
  useQuery({
    queryKey: adminQueryKeys.all,
    queryFn: () =>
      adminServiceV2.post("/users", { queries: [{ typeQuery: { type: "TYPE_HUMAN" } }] }).then(async (response) => {
        const orgMembers = await getOrgMembers();
        const orgs = await getOrginizations();

        const humanUsers = response.result.filter((user: ActiveUser) => !!user.human);

        const activeUsersData: ActiveUserData[] = humanUsers.map((item: ActiveUser) => ({
          updatedAt: item.details.changeDate,
          email: item.human?.email.email,
          firstName: item.human?.profile.givenName,
          fullName: `${item.human?.profile.givenName} ${item.human?.profile.familyName}`,
          id: item.userId,
          lastName: item.human?.profile.familyName,
          status: item.state,
          userId: item.userId,
          orgId: item.details.resourceOwner,
        }));
        const usersListDataPresorted = activeUsersData.map((t1: ActiveUserData) => ({
          ...t1,
          ...orgMembers.find((t2: OrgMembershipList) => t2.userId === t1.userId),
        }));

        const usersGroupByOrg = usersListDataPresorted.reduce((acc: Record<string, ActiveUserData[]>, curr) => {
          const key = curr && curr.orgId;
          const newObj = acc;
          newObj[key] = newObj[key] || [];
          newObj[key].push(curr);
          return newObj;
        }, {});

        return {
          totalCount: humanUsers.length,
          data: usersGroupByOrg,
          orgs,
        };
      }),
    ...{ refetchOnWindowFocus: false, enabled: isAdmin },
  });

export const useCreateUser = () =>
  useMutation({
    mutationFn: async (data: NewUserData) => {
      const { formData } = data;
      const { isAdmin, ...userNameData } = formData;
      const response: NewUserImportResponse = await adminServiceV1.post("/users/human/_import", userNameData);
      const { userId } = response;

      if (isAdmin) {
        const roleObject = {
          userId,
          roles: [ZitadelRoles.ADMIN],
        };
        await adminServiceV1.post("/orgs/me/members", roleObject);

        const currtUserOrgId = AS.getCurrentUserOrganizationId();
        const projects = await searchGrantedProjects();
        const currentUserGrantedProject = projects.find((item: ProjectType) => item.projectOwnerId === currtUserOrgId);
        const { grantId, projectId } = currentUserGrantedProject;
        return adminServiceV1.post(`/users/${userId}/grants`, {
          projectId,
          projectGrantId: grantId,
          roleKeys: [ZitadelRoles.CUSTOMER_ADMIN],
        });
      }
      return null;
    },
  });

export const useEditUser = () =>
  useMutation({
    mutationFn: (data: { formData: UserFormData; userId: string }) =>
      adminServiceV1.put(`/users/${data.userId}/profile`, data.formData),
  });

export const useEditRole = () =>
  useMutation({
    mutationFn: async (data: EditRolePayload) => {
      const { isUserAdmin, userId } = data;

      const userGrants = await getUserGrants();
      const selectedUserGrantId = userGrants?.find((item) => item.userId === userId)?.id;

      if (isUserAdmin) {
        const orgRoleObject = {
          userId,
          roles: [ZitadelRoles.ADMIN],
        };
        await adminServiceV1.post("/orgs/me/members", orgRoleObject);

        const currtUserOrgId = AS.getCurrentUserOrganizationId();
        const projects = await searchGrantedProjects();
        const currentUserGrantedProject = projects.find((item: ProjectType) => item.projectOwnerId === currtUserOrgId);
        const { grantId, projectId } = currentUserGrantedProject;

        return adminServiceV1.post(`/users/${userId}/grants`, {
          projectId,
          projectGrantId: grantId,
          roleKeys: [ZitadelRoles.CUSTOMER_ADMIN],
        });
      }
      // remove customer admin
      if (selectedUserGrantId) await adminServiceV1.remove(`/users/${userId}/grants/${selectedUserGrantId}`);
      // remove org owner
      return adminServiceV1.remove(`/orgs/me/members/${userId}`);
    },
  });

export const useIsUserGrantCustomerAdmin = ({ userId }: { userId?: string }) =>
  useQuery<{
    result: Array<{
      userId: string;
      roleKeys: string[];
    }>;
  }>({
    queryKey: ["members", "_search", userId],
    queryFn: () =>
      adminServiceV1.post("/users/grants/_search", {
        queries: [{ userIdQuery: { userId } }],
      }),
    enabled: !!userId,
  });

export const useDeactivateUser = () =>
  useMutation({
    mutationFn: (data: DeactivateUserPayload) => adminServiceV1.post(`/users/${data.userId}/_deactivate`),
  });

export function reloadUsers() {
  // zitadel api needs some delay
  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: adminQueryKeys.all });
  }, ZITADEL_RELOAD_DELAY);
}
